import React from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Heading,
  Text,
  HStack,
  FormErrorMessage,
} from '@chakra-ui/react'
import { Formik, Form, Field, FieldProps } from 'formik'
import * as Yup from 'yup'
import { CountrySelector } from './CountrySelector'
interface StoreDetailsFormProps {
  selectedPlan: string
  onBack: () => void
  onCancel: () => void
  hideCancelButton?: boolean;
  buttonSubmitText?: string;
  onSubmit: (storeDetails: {
    domain: string
    platform: string
    countries: string[]
  }) => void
  showSteps?: boolean
  initialValues?: {
    domain?: string
    platform?: string
    countries?: string[]
  }
  showBackToPlan?: boolean
}
const validationSchema = Yup.object({
  domain: Yup.string()
    .required('Domain is required')
    .matches(
      /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/,
      'Enter a valid domain (e.g., example.com) without https at the front, no slashes',
    ),
  platform: Yup.string().required('Platform is required'),
  countries: Yup.array()
    .of(Yup.string())
    .min(1, 'Select at least one country')
    .required('Countries are required'),
})
export const StoreDetailsForm = ({
  selectedPlan,
  onBack,
  onCancel,
  onSubmit,
  hideCancelButton = false,
  showSteps = true,
  initialValues = {},
  buttonSubmitText = "Save Store",
  showBackToPlan = true,
}: StoreDetailsFormProps) => {
  const defaultValues = {
    domain: initialValues.domain || '',
    platform: initialValues.platform || 'shopify',
    countries: initialValues.countries || [],
  }
  const handleSubmit = async (
    values: typeof defaultValues,
    {
      setSubmitting,
    }: {
      setSubmitting: (isSubmitting: boolean) => void
    },
  ) => {
    try {
      await onSubmit(values)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setSubmitting(false)
    }
  }
  return (
    <Box bg="gray.900" minH={showSteps ? '100vh' : 'auto'} p={8}>
      <Box maxW="2xl" mx="auto">
        <Formik
          initialValues={defaultValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, setFieldValue, values }) => (
            <Form>
              <VStack spacing={8} align="stretch">
                <Box textAlign="center">
                  <Heading size="xl" color="white" mb={2}>
                    Store Details
                  </Heading>
                  {/* <Text color="gray.400">Selected Plan: {selectedPlan}</Text> */}
                </Box>
                <Box
                  bg="gray.800"
                  p={6}
                  borderRadius="lg"
                  border="1px"
                  borderColor="gray.700"
                >
                  <VStack spacing={6}>
                    <Field name="domain">
                      {({ field, form }: FieldProps) => (
                        <FormControl
                          isInvalid={!!errors.domain && touched.domain}
                        >
                          <FormLabel color="gray.300">Store Domain</FormLabel>
                          <Input
                            {...field}
                            placeholder="example.com"
                            bg="gray.700"
                            border="none"
                            color="white"
                            _focus={{
                              borderColor: errors.domain
                                ? 'red.500'
                                : 'blue.500',
                              boxShadow: errors.domain
                                ? '0 0 0 1px red.500'
                                : '0 0 0 1px blue.500',
                            }}
                          />
                          <FormErrorMessage>{errors.domain}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="platform">
                      {({ field, form }: FieldProps) => (
                        <FormControl
                          isInvalid={!!errors.platform && touched.platform}
                        >
                          <FormLabel color="gray.300">Platform</FormLabel>
                          <Select
                            {...field}
                            bg="gray.700"
                            border="none"
                            color="white"
                            _focus={{
                              borderColor: errors.platform
                                ? 'red.500'
                                : 'blue.500',
                              boxShadow: errors.platform
                                ? '0 0 0 1px red.500'
                                : '0 0 0 1px blue.500',
                            }}
                          >
                            <option value="">Select Platform</option>
                            <option value="shopify">Shopify</option>
                            <option value="wordpress">Wordpress</option>
                            <option value="custom">Custom</option>
                          </Select>
                          <FormErrorMessage>{errors.platform}</FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <FormControl
                      isInvalid={!!errors.countries && touched.countries}
                    >
                      <FormLabel color="gray.300">This Store Sells In</FormLabel>
                      <CountrySelector
                        selectedCountries={values.countries}
                        onChange={(countries) =>
                          setFieldValue('countries', countries)
                        }
                      />
                      <FormErrorMessage>{errors.countries}</FormErrorMessage>
                    </FormControl>
                  </VStack>
                </Box>
                <HStack justify="center" spacing={4}>


                {
                  !hideCancelButton && (
                    <Button
                    variant="ghost"
                    color="gray.400"
                    onClick={onCancel}
                    _hover={{
                      color: 'white',
                    }}
                    isDisabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  )
                }
             
              
                  <Button
                    type="submit"
                    bg="#ff3d00"
                    color="white"
                    isLoading={isSubmitting}
                    loadingText="Saving..."
                    _hover={{
                      bg: '#e53700',
                      boxShadow: '0 0 20px rgba(255, 61, 0, 0.5)',
                    }}
                  >
                    {buttonSubmitText}
                  </Button>
                </HStack>
              </VStack>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  )
}
